// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-nocheck
import {Injectable} from '@angular/core';
import {fallbackCopyImageToClipboard} from "@utils/image";
import { b64toBlob } from "@utils/index";
import {logger} from "@services/logger/logger.service";

/// How to paste text
/// https://web.dev/patterns/clipboard/paste-text/

interface IClipboardTextAreaControl {
    textarea: HTMLTextAreaElement,
    remove: () => void,
}

@Injectable({
    providedIn: 'root'
})
export class ClipboardService {

    constructor() {
        if (!window) {
            throw new Error('Clipboard API unavailable');
        }
    }

    public copyTo(text: string): void {
        const copied = this.copyToWithDeprecatedMethod(text);
        if (copied) {
            return;
        }

        this.askClipboardWritePermission().then(value => {
            if (value) {
                navigator.clipboard.writeText(text)
            }
        });
    }

    public async copyImgTo(imgHtmlTag: string): Promise<void> {
        try {
            const isPermissionAllowed = await this.askClipboardWritePermission();
            const parser = new DOMParser();
            const doc = parser.parseFromString(imgHtmlTag, 'text/html');

            const img = doc.querySelector('img');

            if (isPermissionAllowed && img.src.startsWith('data:')) {
                const midIdx = img.src.indexOf(';base64,');
                const contentType = img.src.substring(5, midIdx);
                const b64Data = img.src.substring(midIdx + 8);
                const blob = b64toBlob(b64Data, contentType);
                const data = [new ClipboardItem({ [blob.type]: blob })];

                await navigator.clipboard.write(data);
            }
            else {
                fallbackCopyImageToClipboard(imgHtmlTag);
            }
        }
        catch (err) {
            logger.error(err);
            logger.error(`Error on writing to clipboard: image`);
        }
    }

    public async copyFrom(): Promise<null | string> {
        const permission = this.askClipboardReadPermission();
        if (permission) {
            try {
                return await navigator.clipboard.readText();
            } catch (e) {
                logger.error(e);
                return this.copyFromWithDeprecatedMethod();
            }
        }

        return this.copyFromWithDeprecatedMethod();
    }

    private copyToWithDeprecatedMethod(text: string): boolean {
        const { remove } = this.createTempTextArea(text)

        try {
            return document.execCommand('copy');
        }
        catch (err) {
            logger.error(`document.execCommand(copy) failed:`, err);
        }
        finally {
            remove();
        }

        return false;
    }

    private copyFromWithDeprecatedMethod(): null | string {
        const { remove, textarea} = this.createTempTextArea('');

        try {
            const res = document.execCommand('paste');
            logger.log(res);
            logger.log(textarea.value);
            return textarea.value;
        }
        catch (err) {
            logger.error(`document.execCommand(copy) failed:`, err);
        }
        finally {
            remove()
        }

        return null;
    }

    private createTempTextArea(text: string): IClipboardTextAreaControl {
        const textarea = document.createElement("textarea");
        textarea.value = text;

        textarea.style.border = "none";
        textarea.style.color = "white";
        textarea.style.backgroundColor = "transparent";
        textarea.style.top = "0";
        textarea.style.left = "0";
        textarea.style.position = "fixed";

        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        return {
            textarea,
            remove: () => document.body.removeChild(textarea),
        }
    }

    private async askClipboardWritePermission(): Promise<void> {
        try {
            const { state } = await navigator.permissions.query({ name: 'clipboard-write' });
            return state === 'granted';
        }
        catch (err) {
            return false;
        }
    }

    private async askClipboardReadPermission(): Promise<void> {
        try {
            const queryOpts = { name: 'clipboard-read', allowWithoutGesture: false };
            const { state } = await navigator.permissions.query(queryOpts);
            return state === 'granted';
        }
        catch (err) {
            return false;
        }
    }
}
